import { ReactNode, useEffect, useState } from 'react';

import {
  Add,
  ArrowBackIos,
  ArrowForwardIos,
  Close,
  FavoriteBorder,
  Visibility,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Link,
  Popover,
  Stack,
  Typography,
} from '@mui/material';

const SABER_MAIS_MAILTO = `mailto:comercial@uqbar.com.br?subject=${encodeURIComponent(
  'Quero saber mais sobre os planos e limites do monitor',
)}`;

interface MonitorStep {
  icon: ReactNode;
  title: string;
  code: 'see' | 'follow' | 'monitor';
  body: ReactNode;
}

const followStepBody = (
  <>
    Use o <strong>Seguir</strong> para receber alertas e novidades sem
    adicioná-lo a uma carteira*.
    <Typography
      component="span"
      variant="caption"
      sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
      *Sujeito a consumo da quantidade disponível de fundos no monitor.{' '}
      <Link
        href={SABER_MAIS_MAILTO}
        underline="hover"
        color="primary"
        fontWeight={600}>
        Saber mais
      </Link>
    </Typography>
  </>
);

const DEFAULT_STEPS: MonitorStep[] = [
  {
    icon: <Visibility fontSize="small" />,
    title: 'Ver',
    code: 'see',
    body: (
      <>
        Use o <strong>Ver</strong> para acessar a página de visualização do
        fundo com seus indicadores e composição.
      </>
    ),
  },
  {
    icon: <FavoriteBorder fontSize="small" />,
    title: 'Seguir',
    code: 'follow',
    body: followStepBody,
  },
  {
    icon: <Add fontSize="small" />,
    title: 'Monitorar',
    code: 'monitor',
    body: (
      <>
        Use o <strong>Monitorar</strong> para adicionar o fundo à uma carteira
        existente ou criar uma nova com o fundo escolhido.
      </>
    ),
  },
];

interface MonitorStepsPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  stepsToShow?: ('see' | 'follow' | 'monitor')[];
}

export function MonitorStepsPopover({
  open,
  anchorEl,
  onClose,
  stepsToShow,
}: MonitorStepsPopoverProps) {
  const [activeStep, setActiveStep] = useState(0);
  const steps = DEFAULT_STEPS.filter((step) =>
    stepsToShow ? stepsToShow.includes(step.code) : true,
  );

  useEffect(() => {
    if (open) {
      setActiveStep(0);
    }
  }, [open, stepsToShow]);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;
  const step = steps[activeStep];

  const handleNext = () => {
    if (isLastStep) {
      onClose();
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevious = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          mt: 1,
          p: 2,
          width: 320,
          maxWidth: '90vw',
          minHeight: '220px',
          borderRadius: 3,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.18)',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}>
      <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: 'rgba(255, 130, 17, 0.12)',
            color: 'primary.main',
            width: 36,
            height: 36,
          }}>
          {step.icon}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700}>
              {step.title}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {activeStep + 1} / {steps.length}
              </Typography>
              <IconButton
                size="small"
                aria-label="Fechar"
                onClick={onClose}
                sx={{ color: 'text.secondary', p: 0.25 }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {step.body}
      </Typography>

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={0.75} alignItems="center">
          {steps.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: index === activeStep ? 18 : 8,
                height: 8,
                borderRadius: 4,
                transition: 'width 0.2s, background-color 0.2s',
                bgcolor:
                  index === activeStep ? 'primary.main' : 'action.disabled',
              }}
            />
          ))}
        </Stack>

        <Box display="flex" alignItems="center" gap={1}>
          {!isFirstStep && (
            <Button
              size="small"
              color="inherit"
              onClick={handlePrevious}
              startIcon={<ArrowBackIos sx={{ fontSize: 12 }} />}
              sx={{ color: 'text.secondary', textTransform: 'none' }}>
              Anterior
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleNext}
            endIcon={
              isLastStep ? undefined : <ArrowForwardIos sx={{ fontSize: 12 }} />
            }
            sx={{ textTransform: 'none' }}>
            {isLastStep ? 'Entendido' : 'Próximo'}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
